import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import type { UserRole } from "@kansai-ai-hub/types";

import { authQueryOptions } from "@/features/auth/auth-queries";
import { LandingPage } from "@/features/landing/landing-page";

/**
 * 未ログインなら Landing を表示。ログイン済みなら role 別 dashboard へリダイレクトする。
 *
 * role guard を持つレイアウト (例: enterprise/route.tsx) で「該当ロール以外なら `/` へ redirect」
 * している。ログイン済みユーザがロール外ルートにアクセスすると `/` に飛ばされるが、
 * そこで Landing が出ると「ログイン済みなのに登録ページが出る」UX 不整合になる。
 * そのため `/` でも role を判定してそれぞれの dashboard に再 redirect する。
 *
 * `Record<UserRole, ...>` で全 UserRole の網羅を型レベルで強制。新ロール追加時にコンパイル
 * エラーで気付けるようにしている。
 */
/**
 * 認証プローブを見切るまでの時間。API の 1 往復＋リトライ 1 回（retryDelay 1000ms）が
 * 収まる長さにしてある。短すぎるとログイン済みユーザーが毎回一瞬 LP を見る。
 */
const AUTH_PROBE_TIMEOUT_MS = 2500;

const ROLE_TO_DASHBOARD: Record<UserRole, "/enterprise" | "/member" | "/admin"> = {
  ENTERPRISE: "/enterprise",
  MEMBER: "/member",
  ADMIN: "/admin",
};

// ログインモーダルの開閉・遷移元を search param で表現する（旧 /login ルートの代替）。
// login=true: ランディング表示時にログインモーダルを自動で開く（認証ガード・/terms からの遷移用）
// redirect: ログイン成功後に戻る先（安全な相対パスのみ useLoginMutation 側で検証）
// `/` はトラフィックの入口であり、不正な search 値（例: /?login=1）でも **公開 LP は必ず出す**
// ため、.catch() で検証失敗時は「search param 無し」に丸めて SearchParamError を発生させない。
const searchSchema = z.object({
  login: z.boolean().optional().catch(undefined),
  redirect: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/")({
  validateSearch: searchSchema,
  beforeLoad: async ({ context }) => {
    // 認証プローブ。一時的失敗（429 / 5xx / network 等）でも **公開 LP は必ず出す**ため、
    // エラーは握りつぶして未ログイン扱いにする（コールドスタート時の全画面エラーを防ぐ・FE-7）。
    // redirect の throw は try の外なので握らない。
    let user = null;
    try {
      /*
       * **時間でも見切る。** catch は「失敗が返ってくる」ことが前提で、プローブの promise が
       * 解決しない失敗モード（networkMode の pause・異常に長いリトライ）には効かない。
       * ここは全トラフィックの入口なので、既定の見た目が「真っ白」になる事故だけは
       * 何があっても避ける。見切った場合は未ログイン扱いで LP を出す
       * （プローブ自体は打ち切らないので、遅れて成功すればキャッシュには入る。
       * ログイン済みユーザーが LP を見る時間が一瞬できるだけで、導線は壊れない）。
       */
      const TIMED_OUT = Symbol("auth-probe-timeout");
      const probe = context.queryClient.ensureQueryData(authQueryOptions);
      // race に負けた側が後から reject しても unhandled rejection にしない
      probe.catch(() => undefined);
      const result = await Promise.race([
        probe,
        new Promise<typeof TIMED_OUT>((resolve) =>
          setTimeout(() => resolve(TIMED_OUT), AUTH_PROBE_TIMEOUT_MS),
        ),
      ]);
      user = result === TIMED_OUT ? null : result;
    } catch {
      user = null;
    }
    if (user) {
      const target = ROLE_TO_DASHBOARD[user.role];
      throw redirect({ to: target });
    }
  },
  component: LandingPage,
});
